/**
 * Boot guards.
 *
 * Both of these exist because of one incident worth not repeating: a missing
 * Vite `envDir` left every VITE_* variable undefined, Firebase threw during
 * module initialisation, and the deployed page rendered as a blank grey
 * rectangle. Hosting still answered HTTP 200, so a curl-based check said the
 * deploy was fine.
 *
 * A blank page is the worst possible failure: it carries no information for
 * the person looking at it and none for whoever gets asked to fix it.
 */

/** The Firebase web config keys every app needs before it can do anything. */
const REQUIRED = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

/**
 * Fails loudly, and early, if the build did not receive its configuration.
 *
 * Called before Firebase initialises so the error names the actual problem
 * rather than surfacing as a cryptic Firebase internal.
 */
export function assertFirebaseEnv(env: Record<string, string | undefined>): void {
  const missing = REQUIRED.filter((k) => !env[k]);
  if (missing.length === 0) return;
  throw new Error(
    `Firebase configuration missing: ${missing.join(', ')}. ` +
      'These come from .env at the repository root and are baked in at build ' +
      "time. If they are absent from a deployed bundle, check `envDir` in the " +
      "app's vite.config.ts — Vite defaults to the app directory, not the repo root.",
  );
}

/**
 * Renders a readable panel into the page instead of leaving it blank.
 *
 * Styled inline on purpose: whatever went wrong may well have been the
 * stylesheet, and this has to work when nothing else does.
 */
export function renderBootError(container: HTMLElement, error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  container.innerHTML = '';

  const box = document.createElement('div');
  box.setAttribute(
    'style',
    [
      'max-width:640px', 'margin:40px auto', 'padding:18px 20px',
      'border:1px solid #8d3c37', 'border-left-width:4px', 'border-radius:4px',
      'background:#f9eceb', 'color:#5e2825',
      'font:14px/1.6 -apple-system,BlinkMacSystemFont,"Helvetica Neue",Arial,sans-serif',
    ].join(';'),
  );

  const title = document.createElement('strong');
  title.textContent = 'This page failed to start.';
  title.setAttribute('style', 'display:block;font-size:15px;margin-bottom:6px');

  const detail = document.createElement('p');
  detail.textContent = message;
  detail.setAttribute(
    'style',
    'margin:0 0 10px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12.5px',
  );

  const help = document.createElement('p');
  help.textContent =
    'Nothing you did caused this. Please let the club know what you were ' +
    'doing when it happened.';
  help.setAttribute('style', 'margin:0;font-size:13px;opacity:.85');

  box.append(title, detail, help);
  container.appendChild(box);
}

/** Mounts an app, turning any boot-time throw into something readable. */
export function boot(containerId: string, mount: (el: HTMLElement) => void): void {
  const el = document.getElementById(containerId);
  if (!el) {
    // Nothing to render into and nothing to render an error into either.
    throw new Error(`Missing #${containerId} element`);
  }
  try {
    mount(el);
  } catch (error) {
    renderBootError(el, error);
    throw error;
  }
}
